import { Autocomplete, InputAdornment, CircularProgress, TextField as MuiTextField, Box, SxProps, Theme, IconButton } from "@mui/material";
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import React, { FC, ReactElement, useEffect, useRef } from "react";
import { ReduxState } from "ra-core";
import { useSelector } from "react-redux";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";


export interface SearchAndAddProps<T extends any = any> {
  term: string,
  setTerm: (x:string)=>void,
  options: T[],
  getOptionLabel: (x:T)=>string,
  onAdd: (x:T|T[])=>void,
  liBoxSx?: SxProps<Theme>,
}

export const SearchAndAdd : FC<SearchAndAddProps<any>> = ({
  term,
  setTerm,
  options,
  getOptionLabel,
  onAdd,
  liBoxSx,
}) => {
  const loading = useSelector<ReduxState>(state => state.admin.loading > 0)
  const { current: cached } = useRef({
    clearClicked: (event: any)=>{},
    n: new Date().getTime(),
  })
  const rootDivRef = useRef<HTMLDivElement>(null)

  const handleAddAll = () => {
    const n1 = new Date().getTime()
    if (n1 - cached.n < 1000) { return }
    cached.n = n1
    const lowerTerm = term.toLowerCase()
    onAdd(
      !term ? options : 
        options.filter(opt => getOptionLabel(opt).toLowerCase().indexOf(lowerTerm) > -1)
    )
    setTimeout(() => cached.clearClicked({}))
  }
  return (
    <Autocomplete freeSolo={true} clearOnEscape 
      options={options}
      inputValue={term}
      getOptionLabel={(x:any) => getOptionLabel(x) ?? ''}
      renderInput={(params) =>{
        const endAdornmentElemChildren : ReactElement[] = (params.InputProps.endAdornment as ReactElement)?.props.children
        if (endAdornmentElemChildren) {
          const clearClicked = (endAdornmentElemChildren[0] as ReactElement)?.props.onClick
          if (clearClicked) {
            cached.clearClicked = clearClicked
          }
          params.InputProps.endAdornment = React.cloneElement(params.InputProps.endAdornment as any, undefined, [
            <IconButton title="Add All" aria-label="Add All" color="secondary" sx={{ p: 0.25, mr: -0.25}} onClick={handleAddAll}>
              <PlaylistAddCheckIcon />
            </IconButton>,
            ...endAdornmentElemChildren
          ])
        } else {
          params.InputProps.endAdornment = 
            <Box className="MuiAutocomplete-endAdornment" 
              sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translate(0, -50%)' }}
            >
              <IconButton title="Add All" aria-label="Add All" color="secondary" sx={{ p: 0.25, mr: -0.25}} onClick={handleAddAll}>
                <PlaylistAddCheckIcon />
              </IconButton>
            </Box>
        }
        return (
          <MuiTextField {...params} size="small" sx={{ width: 400 }} ref={rootDivRef}
            placeholder="Search to add..."
            InputProps={{...params.InputProps,
              ...(loading && {
              startAdornment: 
                <InputAdornment position="start"><CircularProgress size={20} /></InputAdornment>
              } as any)
            }}
          />)
      }}
      renderOption={(props, option, { inputValue }) => {
        const matches = match(getOptionLabel(option), inputValue, {insideWords:true});
        const parts = parse(getOptionLabel(option), matches);
        return (
          <li {...props}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordWrap: 'normal', whiteSpace: 'nowrap', ...liBoxSx }}>
              {parts.map((part, index) => (
                <span
                  key={index}
                  style={{
                    fontWeight: part.highlight ? 700 : 400,
                  }}
                >
                  {part.text}
                </span>
              ))}
            </Box>
          </li>
        );
      }}
      onInputChange={(e, value:string) => { setTerm(value) }}
      onChange={(e, value, reason) => {
        if (reason === 'selectOption') {
          onAdd(value)
          setTimeout(() => cached.clearClicked({}))
        } else if (reason === 'createOption') {
          handleAddAll()
        }
      }}
    />
  )
}